"use client";

import { Fragment, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "@/components/feature-icons";

type Cell = true | string;

type Group = {
  title: string;
  rows: { label: string; small: Cell; team: Cell; large: Cell }[];
};

const PLANS = ["Small", "Team", "Large"] as const;

const GROUPS: Group[] = [
  {
    title: "Access",
    rows: [{ label: "Users", small: "Up to 10", team: "Up to 20", large: "Custom" }],
  },
  {
    title: "Intelligence & data",
    rows: [
      { label: "Unlimited included searches*", small: true, team: true, large: true },
      { label: "People Intelligence", small: true, team: true, large: true },
      { label: "Address Intelligence", small: true, team: true, large: true },
      { label: "Connections", small: true, team: true, large: true },
      { label: "Company Intelligence", small: true, team: true, large: true },
      { label: "Company Credit Checks", small: true, team: true, large: true },
      { label: "Property Intelligence", small: true, team: true, large: true },
      { label: "Land Registry", small: true, team: true, large: true },
      { label: "Contact Intelligence", small: true, team: true, large: true },
      { label: "Tracing", small: true, team: true, large: true },
      { label: "Data cleansing & enrichment", small: true, team: true, large: true },
      { label: "Bulk processing", small: true, team: true, large: true },
      { label: "Structured exports", small: true, team: true, large: true },
      { label: "Auditable search history", small: true, team: true, large: true },
    ],
  },
  {
    title: "Technology",
    rows: [
      { label: "API access", small: "Included", team: "Included", large: "Included" },
      { label: "CRM integration", small: "Included", team: "Included", large: "Included" },
      { label: "Casework integration", small: "Included", team: "Included", large: "Included" },
      { label: "Modular integration", small: "Included", team: "Included", large: "Included" },
    ],
  },
  {
    title: "Implementation & consultancy",
    rows: [
      { label: "Workflow analysis", small: "Included", team: "Included", large: "Included" },
      { label: "Integration consultancy", small: "Included", team: "Included", large: "Included" },
      { label: "API implementation support", small: "Included", team: "Included", large: "Included" },
      { label: "Field & result mapping", small: "Included", team: "Included", large: "Included" },
      { label: "Workflow configuration", small: "Included", team: "Included", large: "Included" },
      { label: "Integration support", small: "Included", team: "Included", large: "Included" },
    ],
  },
  {
    title: "Terms",
    rows: [
      { label: "Monthly billing", small: true, team: true, large: "Agreed" },
      { label: "Fixed-term commitment", small: "None", team: "None", large: "Agreed" },
      { label: "KYC / AML", small: "£1.50 / call", team: "£1.50 / call", large: "Contact us" },
    ],
  },
];

function CellValue({ value }: { value: Cell }) {
  if (value === true) {
    return <CheckIcon className="mx-auto h-4 w-4 text-brand-cyan" aria-label="Included" />;
  }
  return <span className="text-sm text-on-surface-variant">{value}</span>;
}

export function PlanComparison() {
  const [openPlan, setOpenPlan] = useState<number | null>(null);

  return (
    <div className="mx-auto max-w-[1000px]">
      {/* Desktop table */}
      <table className="hidden w-full border-separate border-spacing-0 text-left md:table">
        <thead>
          <tr>
            <th
              scope="col"
              className="w-[46%] border-b-2 border-outline-variant py-4 pr-4 align-bottom text-label-md font-semibold text-brand-navy"
            >
              Capability
            </th>
            {PLANS.map((plan) => (
              <th
                key={plan}
                scope="col"
                className={`border-b-2 border-outline-variant py-4 text-center align-bottom text-label-md font-semibold text-brand-navy ${
                  plan === "Team" ? "bg-[#f1f3fb]" : ""
                }`}
              >
                {plan}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {GROUPS.map((group) => (
            <Fragment key={group.title}>
              <tr>
                <td colSpan={4} className="pt-8 pb-2">
                  <span className="text-label-sm font-semibold uppercase tracking-[0.14em] text-brand-electric">
                    {group.title}
                  </span>
                </td>
              </tr>
              {group.rows.map((row) => (
                <tr key={row.label}>
                  <th
                    scope="row"
                    className="border-b border-outline-variant/50 py-3 pr-4 text-sm font-normal text-brand-navy"
                  >
                    {row.label}
                  </th>
                  <td className="border-b border-outline-variant/50 py-3 text-center">
                    <CellValue value={row.small} />
                  </td>
                  <td className="border-b border-outline-variant/50 bg-[#f1f3fb] py-3 text-center">
                    <CellValue value={row.team} />
                  </td>
                  <td className="border-b border-outline-variant/50 py-3 text-center">
                    <CellValue value={row.large} />
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>

      {/* Mobile: expandable per-plan cards */}
      <div className="space-y-4 md:hidden">
        {PLANS.map((plan, pi) => {
          const isOpen = openPlan === pi;
          const key = plan.toLowerCase() as "small" | "team" | "large";
          return (
            <div
              key={plan}
              className={`overflow-hidden rounded-xl border bg-white ${
                plan === "Team" ? "border-brand-cyan/50" : "border-outline-variant"
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenPlan(isOpen ? null : pi)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
              >
                <span className="text-base font-bold text-brand-navy">{plan}</span>
                <ChevronDownIcon
                  className={`h-5 w-5 shrink-0 text-brand-electric transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isOpen && (
                <dl className="border-t border-outline-variant/60 px-4 pb-3">
                  {GROUPS.map((group) => (
                    <Fragment key={`${plan}-${group.title}`}>
                      <div className="pt-4 pb-1 text-label-sm font-semibold uppercase tracking-[0.12em] text-brand-electric">
                        {group.title}
                      </div>
                      {group.rows.map((row) => (
                        <div
                          key={`${plan}-${row.label}`}
                          className="flex items-center justify-between gap-4 border-b border-outline-variant/40 py-2.5 last:border-b-0"
                        >
                          <dt className="text-sm text-brand-navy">{row.label}</dt>
                          <dd className="shrink-0">
                            <CellValue value={row[key]} />
                          </dd>
                        </div>
                      ))}
                    </Fragment>
                  ))}
                </dl>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
